import { capitalize } from 'lodash';
import { cn } from '@/utils';
import { SKILL_LEVELS, WORDS } from '@/constants';
import { getSkillLevelLabel } from '@/domains';
import { TScoreSkillLevel } from '@/types';
import React from 'react';

export type SkillLevel = TScoreSkillLevel;
export interface SkillLevelBadgeProps {
  /** The skill level to display */
  level: SkillLevel;
  /** Additional CSS classes */
  className?: string;
  /** Optional custom label override */
  label?: string;
}

/**
 * SkillLevelBadge Component
 *
 * Displays skill level indicators with appropriate colors
 * based on the Figma design system.
 */
export const SkillLevelBadge: React.FC<SkillLevelBadgeProps> = ({
  level,
  className,
  label,
}) => {
  const getSkillConfig = () => {
    switch (level) {
      case SKILL_LEVELS.BEGINNER:
      case SKILL_LEVELS.AWARENESS:
      case SKILL_LEVELS.NOVICE:
        return {
          label: label || getSkillLevelLabel(level),
          bgColor: 'bg-accent-orange-300',
          textColor: 'text-neutral-black',
        };
      case SKILL_LEVELS.INTERMEDIATE:
      case SKILL_LEVELS.SKILLED:
        return {
          label: label || getSkillLevelLabel(level),
          bgColor: 'bg-accent-purple-300',
          textColor: 'text-neutral-black',
        };
      case SKILL_LEVELS.ADVANCED:
        return {
          label: label || getSkillLevelLabel(level),
          bgColor: 'bg-accent-blue-300',
          textColor: 'text-neutral-black',
        };
      case SKILL_LEVELS.EXPERT:
        return {
          label: label || getSkillLevelLabel(level),
          bgColor: 'bg-accent-green-300',
          textColor: 'text-neutral-black',
        };
      default:
        return {
          label: label || capitalize(WORDS.UNKNOWN),
          bgColor: 'bg-neutral-gray-100',
          textColor: 'text-neutral-gray-600',
        };
    }
  };

  const config = getSkillConfig();

  return (
    <div 
      className={cn(
        'inline-flex items-center justify-center px-2 py-1 rounded-md overflow-hidden flex-shrink-0',
        config.bgColor,
        className
      )}
    >
      <span 
        className={cn(
          'text-xs font-medium leading-4 text-center whitespace-nowrap',
          config.textColor
        )}
      >
        {config.label}
      </span>
    </div>
  );
};

SkillLevelBadge.displayName = 'SkillLevelBadge';